import React from "react"
import { UnknownAction } from "redux"
import { ThunkDispatch } from "redux-thunk"
import moment from "moment-jalaali"
import theme from "lib/material-ui/theme"
import { privacyOptions } from "constants/options"
import { PathInfo } from "types/general.types"
import { PremiumOnboardingSlide } from "types/premium.types"
import { CategorizedNotifications, WishamNotification } from "types/notification.types"
import { PATH_INFOS } from "utils/navigatorUtils"
import { getPremiumSlidesForActiveFeatures } from "utils/PremiumUtils"
import { updatePremiumCurrentOnboardingSlide, updateShowPremiumOnboarding } from "appRedux/slices/premiumSlice"

type CustomEventObject = {
  target: { files: File[] }
}

export function uploadImage(event: React.ChangeEvent<HTMLInputElement> | CustomEventObject,
                            element: HTMLElement,
                            setImage: React.SetStateAction<any> | undefined,
                            onChange: (value: File) => void) {
  if (!event.target.files || !event.target.files.length) return
  const file = event.target.files[0]

  const fileReader = new FileReader()

  fileReader.onload = () => {
    if (!element) return
    element.style.background = `url(${fileReader.result as string}), ${theme.palette.gray[16]}`
    if (setImage) {
      setImage(fileReader.result as string)
    }
  }

  fileReader.readAsDataURL(file)
  onChange(file)

  return {
    file,
    fileReader
  }
}

export function getPrivacyOptionDetail(privacy: Privacy) {
  return privacyOptions.find(option => option.key === privacy)
}

export function isListEmpty(list: WishList | null) {
  if (!list) return false
  if (!list?.listItems) return true
  return list?.listItems?.length === 0
}

export function categorizeNotifications(notifications: WishamNotification[]): CategorizedNotifications {
  const categorized: CategorizedNotifications = {
    today: [],
    yesterday: [],
    lastWeek: [],
    older: []
  }

  const now = moment()
  const today = now.startOf("day")
  const yesterday = moment().subtract(1, "day").startOf("day")
  const lastWeek = moment().subtract(7, "days").startOf("day")

  notifications.forEach((notif) => {
    const notificationDate = moment(notif.date)

    let category: keyof CategorizedNotifications

    switch (true) {
      case notificationDate.isSame(today, "day"):
        category = "today"
        break
      case notificationDate.isSame(yesterday, "day"):
        category = "yesterday"
        break
      case notificationDate.isAfter(lastWeek, "day"):
        category = "lastWeek"
        break
      default:
        category = "older"
    }

    categorized[category].push(notif)
  })

  return categorized
}

export function getActiveProfileTabIndex(selectedTabIndex: number, userProfile: Profile | null) {
  if (!userProfile || selectedTabIndex > -1) return selectedTabIndex

  const listsCount = userProfile?.wishlistCount || 0
  const itemsCount = userProfile?.itemsCount || 0

  if (listsCount === 0 && itemsCount > 0) {
    return 1
  } else {
    return 0
  }
}

export function searchUser(users: UserDto[] | UserFollowDto[] | null, searchedValue: string): (any[]) {
  const lowerCasedSearchedValue = searchedValue?.toLowerCase() || ""
  const filteredUsers = users?.filter(user => (user?.name?.toLowerCase()?.includes(lowerCasedSearchedValue) || user?.handle?.toLowerCase()?.includes(lowerCasedSearchedValue)))
  return filteredUsers || []
}

export function shouldRenderUsername(pageInfo: PathInfo, user: Profile | null) {
  if (!user) return false
  return pageInfo &&
    pageInfo?.path?.includes(":handle") &&
    pageInfo?.path !== PATH_INFOS.profile.path &&
    pageInfo?.dynamicPathParts?.handle !== user?.handle
}

export function handlePremiumOnboarding(event: React.MouseEvent<any>, feature: string, dispatch: ThunkDispatch<any, any, UnknownAction>) {
  dispatch(updateShowPremiumOnboarding(true))
  const premiumFeatureIndex = getPremiumSlidesForActiveFeatures().findIndex((feat) => feat.key === feature)
  if (premiumFeatureIndex !== -1) {
    dispatch(updatePremiumCurrentOnboardingSlide(premiumFeatureIndex as PremiumOnboardingSlide))
  }
  event.stopPropagation()
}
